import { OpenPopoverEvents, OnOpenChangeData, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { M365PulseSearchBox, SearchResultItem } from "Shared/Components";
import { SearchBoxPopover } from "./SearchBoxPopover";
import { observer } from "mobx-react";
import { useInRouterContext } from "react-router";
import { useRootStore } from "Store/RootStore";
import { useLogging, useSmallScreen } from "Shared/Hooks";
import { useUniversalSearchStyles } from "./UniversalSearch.styles";
import { LogCategory, RenderingStatus } from "Enumerations";
import { SearchGlobal } from "./SearchGlobal";
import { UniversalSearchFilters } from "./UniversalSearchFilters";
import { parseQuery } from "Store/utils/searchQueryParser";
import { noResultsItem } from "Store/UniversalSearchStore";
import { Filter24Filled } from "@fluentui/react-icons";
import { useSearchTopTrends } from "./useSearchTopTrends";
import { useSearchResultList } from "../../../../Shared/Hooks/Core/useUniversalSearchList";

const UniversalSearchBase = () => {
    const inRouter = useInRouterContext();
    const searchStore = useRootStore("UniversalSearchStore");
    const authStore = useRootStore("AuthStore");
    const isSmallScreen = useSmallScreen();
    const classNames = useUniversalSearchStyles({ isSmallScreen: isSmallScreen });
    const { trackEvent } = useLogging(LogCategory.Core);
    const [value, setValue] = useState("");
    const [enterQueued, setEnterQueued] = useState(false);

    const { trendingSearchesData } = useSearchTopTrends(authStore.loginStatus === RenderingStatus.Ready);

    const filterItem: SearchResultItem = {
        id: "filter-item",
        title: searchStore.isGlobalSearch ? "Filter in this page" : "Search in all M365 Pulse",
        description: "",
        category: "",
        icon: Filter24Filled,
        onClick: () => {
            searchStore.setGlobalSearch(!searchStore.isGlobalSearch);
            searchStore.onSearch(searchStore.searchWord);
            searchStore.setFilter(null);
        }
    };

    const searchClass = useRef({
        input: classNames.input,
        popover: classNames.popover,
        menuitem: classNames.menuitem
    }).current;
    const [open, setOpen] = React.useState(true);
    const onSearchChange = useCallback(
        (searchWord: string) => {
            const { text, filter } = parseQuery(searchWord);

            if (filter) {
                searchStore.setFilter(filter);
            } else {
                searchStore.setFilter(null);
            }
            searchStore.searchWord = text;
            searchStore.onSearch(searchWord);
            setValue(searchWord);
        },
        [searchStore, setValue]
    );

    const searchResultList: SearchResultItem[] = useSearchResultList({
        trendingSearchesData,
        setValue,
        filterItem
    });

    const firstSearchItem = useMemo(
        () => searchStore.resultList.find((item) => item.category !== "Recent Searches" && item.category !== "Trending Searches"),
        [searchStore.resultList]
    );

    useEffect(() => {
        setValue(searchStore.searchWord);
    }, [searchStore, searchStore.filters, searchStore.onSearch, searchStore.searchWord]);

    useEffect(() => {
        if (
            searchStore.searchWord &&
            searchStore.resultList.length === 1 &&
            searchStore.resultList[0] === noResultsItem &&
            searchStore.status === RenderingStatus.Ready
        ) {
            trackEvent("16b0c189-b4ed-4e59-9217-23078984b3aa", "UniversalSearchNoResult", {
                searchWord: searchStore.searchWord
            });
        }
    }, [searchStore, searchStore.resultList, searchStore.searchWord, searchStore.status, trackEvent]);

    useEffect(() => {
        if (enterQueued && searchStore.searchStatus === "success" && firstSearchItem && firstSearchItem.onClick) {
            firstSearchItem.onClick();
            setEnterQueued(false);
        }
    }, [enterQueued, searchStore.searchStatus, searchStore.resultList, firstSearchItem]);

    const onClosePopover = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const onResultsMenuOpenChange = useCallback(
        (e: OpenPopoverEvents, data: OnOpenChangeData) => {
            if (searchStore.searchWord && searchStore.resultList.length > 0 && searchStore.status === RenderingStatus.Ready && !data.open) {
                const resultsNotSelected = searchStore.resultList.map(({ onClick, ...rest }) => ({ ...rest }));
                trackEvent("e3bc1cfa-7729-47f2-b8c0-9290fcd29afe", "UniversalSearchResultsNotSelected", {
                    searchWord: searchStore.searchWord,
                    results: resultsNotSelected
                });
            }
        },
        [searchStore, trackEvent]
    );

    const onSearchKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== "Enter") {
                return;
            }

            if (!searchStore.isGlobalSearch) {
                searchStore.setGlobalSearch(true);
            }

            if (searchStore.searchStatus === "pending" || !firstSearchItem) {
                setEnterQueued(true);
                return;
            }

            firstSearchItem.onClick?.();
        },
        [searchStore, firstSearchItem]
    );

    return (
        <div className={classNames.searchResourceContainer} data-tourstep="searchViews">
            <Popover open={searchStore.popover ? open : false} withArrow positioning="below">
                <PopoverTrigger>
                    <M365PulseSearchBox
                        searchResultList={searchResultList}
                        loadingStatus={searchStore.status}
                        onSearchChange={onSearchChange}
                        onValueChange={setValue}
                        placeholder={searchStore.placeholder}
                        className={searchClass}
                        resultType="category"
                        value={value}
                        onOpenResultsChange={onResultsMenuOpenChange}
                        searchingStatus={enterQueued ? searchStore.searchStatus : undefined}
                        customPopoverContent={
                            !(searchStore.searchWord.startsWith("is:") && !searchStore.searchWord.includes(" ")) && <UniversalSearchFilters />
                        }
                        customOnKeyDown={onSearchKeyDown}
                        clearable
                        disableAutoOpen
                    />
                </PopoverTrigger>
                <PopoverSurface className={classNames.popoverContainer}>
                    {searchStore.popover && <SearchBoxPopover popoverContent={searchStore.popover} closePopoverCallback={onClosePopover} />}
                </PopoverSurface>
            </Popover>

            {inRouter && <SearchGlobal />}
        </div>
    );
};

export const UniversalSearch = observer(UniversalSearchBase);
